import { Button } from '@cibli/shared';
import { animate, createTimeline, utils } from 'animejs-v4';
import { Check } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MainLayout from '@/features/common/components/MainLayout';

interface Props {
  onGenerated: () => void;
}

export default function CVGenerationScreen({ onGenerated }: Props) {
  const { t } = useTranslation();
  const [isReady, setIsReady] = useState(false);
  const documentCardRef = useRef<HTMLElement>(null);
  const loadingLabelRef = useRef<HTMLParagraphElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<SVGCircleElement>(null);
  const percentRef = useRef<HTMLElement>(null);
  const readyStateRef = useRef<HTMLDivElement>(null);
  const successMarkRef = useRef<HTMLDivElement>(null);
  const modifyButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const progressBar = progressBarRef.current;
    const ring = ringRef.current;
    const percent = percentRef.current;
    const documentCard = documentCardRef.current;
    const loadingLabel = loadingLabelRef.current;

    if (!progressBar || !ring || !percent || !documentCard || !loadingLabel) return;

    const circumference = 314;
    const value = { current: 0 };
    const updateProgress = (current: number) => {
      percent.textContent = `${current}%`;
      progressBar.setAttribute('aria-valuenow', String(current));
      ring.style.strokeDashoffset = String(circumference - (circumference * current) / 100);
    };

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      updateProgress(100);
      setIsReady(true);
      return;
    }

    const intro = createTimeline({ defaults: { ease: 'out(3)' } })
      .add(documentCard, { opacity: [0, 1], y: [26, 0], duration: 700 })
      .add(progressBar, { opacity: [0, 1], y: [16, 0], duration: 500 }, '-=350')
      .add(loadingLabel, { opacity: [0, 1], duration: 350 }, '-=220');

    const counter = animate(value, {
      current: 100,
      duration: 3600,
      ease: 'out(2)',
      modifier: utils.round(0),
      onUpdate: () => updateProgress(value.current),
      onComplete: () => setIsReady(true),
    });

    return () => {
      intro.pause();
      counter.pause();
    };
  }, []);

  useEffect(() => {
    if (!isReady) return;

    const readyState = readyStateRef.current;
    const successMark = successMarkRef.current;
    const modifyButton = modifyButtonRef.current;
    if (!readyState || !successMark || !modifyButton) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const readyAnimation = animate(readyState, {
      opacity: [0, 1],
      y: [22, 0],
      duration: 700,
      ease: 'out(4)',
    });
    const markAnimation = animate(successMark, {
      scale: [0.82, 1.08, 1],
      duration: 900,
      ease: 'outElastic(1, .55)',
    });
    const buttonAnimation = animate(modifyButton, {
      opacity: [0, 1],
      y: [14, 0],
      duration: 500,
      delay: 280,
      ease: 'out(3)',
    });

    return () => {
      readyAnimation.pause();
      markAnimation.pause();
      buttonAnimation.pause();
    };
  }, [isReady]);

  return (
    <MainLayout
      align="center"
      className="h-[calc(100dvh_-_72px)] sm:h-[calc(100dvh_-_96px)] max-h-[1920px] min-h-0 overflow-hidden bg-white text-black"
      padding="p-4 pb-6 sm:p-8"
    >
      <section
        aria-atomic="true"
        aria-live="polite"
        className="flex h-full w-full max-w-3xl flex-col items-center justify-start pt-[calc(clamp(10rem,20dvh,18rem)_-_72px)] sm:pt-[calc(clamp(10rem,20dvh,18rem)_-_96px)] text-center"
      >
        <p className="mb-4 text-xs font-bold uppercase tracking-[0.14em] text-[#009f7f] sm:text-base">
          {t('CVGeneration.badge')}
        </p>
        <h1 className="font-black text-3xl uppercase leading-tight tracking-[-0.045em] sm:text-5xl">
          {t('CVGeneration.title')}
        </h1>

        {!isReady ? (
          <div
            className="flex w-full flex-col items-center"
            aria-label={t('CVGeneration.progressAria')}
          >
            <article
              ref={documentCardRef}
              aria-hidden="true"
              className="relative mt-12 h-56 w-44 rotate-[-2deg] rounded-[10px] border-2 border-[#254441] bg-white p-6 opacity-0 shadow-[14px_16px_0_#21c294] sm:mt-16 sm:h-80 sm:w-64"
            >
              <span className="absolute left-6 top-6 h-2.5 w-1/3 rounded-full bg-[#21c294]" />
              <span className="absolute left-6 top-12 h-1.5 w-1/2 rounded-full bg-[#254441]/25" />
              <div className="mt-16 flex flex-col gap-3.5">
                {[92, 75, 84, 56, 89].map(width => (
                  <span
                    key={width}
                    className="block h-1.5 rounded-full bg-[#254441]/20"
                    style={{ width: `${width}%` }}
                  />
                ))}
              </div>
            </article>

            <div
              ref={progressBarRef}
              aria-valuemax={100}
              aria-valuemin={0}
              aria-valuenow={0}
              className="mt-12 flex items-center gap-5 opacity-0 sm:gap-7"
              role="progressbar"
            >
              <svg
                aria-hidden="true"
                className="size-24 -rotate-90 sm:size-29"
                viewBox="0 0 116 116"
              >
                <circle cx="58" cy="58" r="50" fill="none" stroke="#fbf7f5" strokeWidth="10" />
                <circle
                  ref={ringRef}
                  cx="58"
                  cy="58"
                  r="50"
                  fill="none"
                  stroke="#21c294"
                  strokeDasharray="314"
                  strokeDashoffset="314"
                  strokeLinecap="round"
                  strokeWidth="10"
                />
              </svg>
              <strong
                ref={percentRef}
                className="min-w-20 text-left text-4xl font-black text-[#254441] sm:min-w-28 sm:text-6xl"
              >
                0%
              </strong>
            </div>
            <p ref={loadingLabelRef} className="mt-5 text-sm text-[#254441] opacity-0 sm:text-lg">
              {t('CVGeneration.message')}
            </p>
          </div>
        ) : (
          <div ref={readyStateRef} className="flex flex-col items-center opacity-0">
            <div
              ref={successMarkRef}
              aria-hidden="true"
              className="mt-12 grid size-24 place-items-center rounded-full bg-[#21c294] text-white shadow-[0_0_0_14px_rgba(33,194,148,0.12)] sm:mt-16 sm:size-32"
            >
              <Check className="size-12 sm:size-16" strokeWidth={2.8} />
            </div>
            <p className="mt-8 max-w-2xl font-black text-3xl leading-tight tracking-[-0.045em] sm:mt-10 sm:text-5xl">
              {t('CVGeneration.ready')}
            </p>
            <p className="mt-5 text-sm text-[#254441] sm:text-lg">{t('CVGeneration.hint')}</p>
            <Button
              ref={modifyButtonRef}
              type="button"
              size="lg"
              onClick={onGenerated}
              className="mt-8 min-h-16 rounded-2xl bg-[#21c294] px-8 text-base font-bold text-white shadow-[0_10px_22px_rgba(33,194,148,0.24)] hover:bg-[#009f7f] sm:mt-11 sm:px-10 sm:text-xl"
            >
              {t('CVGeneration.editCta')}
            </Button>
          </div>
        )}
      </section>
    </MainLayout>
  );
}
